import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminCustomizationOption } from '@/hooks/admin/useAdmin';
import { useCustomizationOptions, useDeleteCustomizationOption } from '@/hooks/admin/useAdminCustomizations';
import CustomizationOptionModal from '@/modals/admin/CustomizationOptionModal';

const CustomizationsAdmin: React.FC = () => {
    const [selectedCustomization, setSelectedCustomization] = useState<AdminCustomizationOption | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');

    const { data: customizations = [], isLoading, error } = useCustomizationOptions();
    const deleteCustomization = useDeleteCustomizationOption();

    const filteredCustomizations = customizations.filter(customization => {
        const matchesSearch = customization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            customization.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterActive === 'all' ||
                            (filterActive === 'active' && customization.is_active) ||
                            (filterActive === 'inactive' && !customization.is_active);
        return matchesSearch && matchesFilter;
    });

    const handleCreateCustomization = () => {
        setSelectedCustomization(undefined);
        setModalOpen(true);
    };

    const handleEditCustomization = (customization: AdminCustomizationOption) => {
        setSelectedCustomization(customization);
        setModalOpen(true);
    };

    const handleDeleteCustomization = async (customization: AdminCustomizationOption) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar la opción de personalización "${customization.name}"?`)) {
            try {
                await deleteCustomization.mutateAsync(customization.id);
            } catch (error) {
                console.error('Error deleting customization:', error);
                alert('Error al eliminar la opción de personalización');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedCustomization(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando opciones de personalización...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar las opciones de personalización</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Personalizaciones</h1>
                        <p className="admin-page-subtitle">
                            Administra las opciones de personalización disponibles
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateCustomization}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nueva Opción
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar opciones..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todas</option>
                        <option value="active">Activas</option>
                        <option value="inactive">Inactivas</option>
                    </select>
                </div>
            </div>

            {/* Customizations Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th>Costo Adicional</th>
                            <th>Orden</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCustomizations.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all'
                                        ? 'No se encontraron opciones con los filtros aplicados'
                                        : 'No hay opciones de personalización registradas'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredCustomizations.map(customization => (
                                <tr key={customization.id}>
                                    <td className="admin-table-name">
                                        {customization.name}
                                    </td>
                                    <td className="admin-table-description">
                                        <span title={customization.description}>
                                            {customization.description.length > 50
                                                ? `${customization.description.substring(0, 50)}...`
                                                : customization.description
                                            }
                                        </span>
                                    </td>
                                    <td className="admin-table-price">
                                        ${customization.additional_cost.toFixed(2)}
                                    </td>
                                    <td className="admin-table-order">
                                        {customization.sort_order}
                                    </td>
                                    <td>
                                        <span className={`admin-status ${customization.is_active ? 'active' : 'inactive'}`}>
                                            {customization.is_active ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditCustomization(customization)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteCustomization(customization)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteCustomization.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Customization Modal */}
            <CustomizationOptionModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                customization={selectedCustomization}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default CustomizationsAdmin;
